<template>
  <div class="integracoes-erp-page">
    <nav class="page-breadcrumb mb-1">
      <feather-icon icon="HomeIcon" size="14" />
      <span class="ml-25">Configurações</span>
      <feather-icon icon="ChevronRightIcon" size="14" class="mx-25" />
      <span class="font-weight-bold">Integração ERP</span>
    </nav>

    <b-card class="mb-1">
      <b-row class="align-items-end">
        <b-col md="4" sm="12">
          <label class="small text-muted">Pesquisar</label>
          <b-form-input v-model="filters.q" size="sm" placeholder="Nome ou URL" @keydown.enter="buscar" />
        </b-col>
        <b-col md="3" sm="6">
          <label class="small text-muted">Status</label>
          <b-form-select v-model="filters.status" size="sm" @change="buscar">
            <b-form-select-option :value="null">Todos</b-form-select-option>
            <b-form-select-option value="ATIVA">Ativa</b-form-select-option>
            <b-form-select-option value="INATIVA">Inativa</b-form-select-option>
          </b-form-select>
        </b-col>
        <b-col md="5" sm="12" class="d-flex align-items-end" style="gap:6px">
          <b-button size="sm" variant="primary" @click="buscar" :disabled="isLoading">
            <feather-icon icon="SearchIcon" size="13" />
          </b-button>
          <b-button size="sm" variant="outline-secondary" @click="limparFiltros">
            <feather-icon icon="XIcon" size="13" />
          </b-button>
          <b-button size="sm" variant="relief-success" class="ml-auto" @click="abrirNova">
            <feather-icon icon="PlusIcon" size="13" class="mr-25" />Nova Integração
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <b-card>
      <div v-if="isLoading" class="text-center py-4"><b-spinner /></div>
      <div v-else-if="!integracoes.length" class="text-center py-4 text-muted">
        <feather-icon icon="InboxIcon" size="36" class="mb-1" /><br>
        Nenhuma integração cadastrada.
      </div>
      <b-table
        v-else
        :items="integracoes"
        :fields="fields"
        hover
        responsive
        small
      >
        <template #cell(nome)="{ item }">
          <strong>{{ item.nome }}</strong>
        </template>
        <template #cell(url)="{ item }">
          <span class="text-truncate d-inline-block" style="max-width:280px" :title="item.url">{{ item.url }}</span>
        </template>
        <template #cell(metodo)="{ item }">
          <b-badge :variant="metodoVariant(item.metodo)">{{ item.metodo }}</b-badge>
        </template>
        <template #cell(status)="{ item }">
          <b-badge :variant="item.status === 'ATIVA' ? 'light-success' : 'light-secondary'">
            {{ item.status === 'ATIVA' ? 'Ativa' : 'Inativa' }}
          </b-badge>
        </template>
        <template #cell(created_at)="{ item }">
          {{ formatDate(item.created_at) }}
        </template>
        <template #cell(ultima_execucao)="{ item }">
          {{ item.ultima_execucao ? formatDate(item.ultima_execucao) : '—' }}
        </template>
        <template #cell(acoes)="{ item }">
          <b-button size="sm" variant="flat-info" class="mr-25" title="Testar" @click="testar(item)">
            <feather-icon icon="ZapIcon" size="13" />
          </b-button>
          <b-button size="sm" variant="flat-primary" class="mr-25" title="Editar" @click="editar(item)">
            <feather-icon icon="EditIcon" size="13" />
          </b-button>
          <b-button size="sm" variant="flat-danger" title="Excluir" @click="confirmarExcluir(item)">
            <feather-icon icon="Trash2Icon" size="13" />
          </b-button>
        </template>
      </b-table>

      <div v-if="meta.totalPages > 1" class="d-flex justify-content-end mt-1">
        <b-pagination
          v-model="currentPage"
          :total-rows="meta.total"
          :per-page="meta.pageSize"
          size="sm"
          @change="onPageChange"
        />
      </div>
    </b-card>

    <IntegracaoModal ref="modalForm" @saved="onSaved" />
    <TestarIntegracaoModal ref="modalTeste" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import IntegracaoModal from './modal/IntegracaoModal.vue';
import TestarIntegracaoModal from './modal/TestarIntegracaoModal.vue';

export default {
  name: 'IntegracoesErp',
  components: { IntegracaoModal, TestarIntegracaoModal },
  data() {
    return {
      filters: { q: '', status: null },
      currentPage: 1,
      fields: [
        { key: 'nome',            label: 'Nome' },
        { key: 'url',             label: 'URL' },
        { key: 'metodo',          label: 'Método' },
        { key: 'status',          label: 'Status' },
        { key: 'created_at',      label: 'Criação' },
        { key: 'ultima_execucao', label: 'Última Execução' },
        { key: 'acoes',           label: '', class: 'text-right', thStyle: { width: '160px' } },
      ],
    };
  },
  computed: {
    ...mapGetters('IntegracaoErp', ['isLoading', 'integracoes', 'meta']),
  },
  created() {
    this.buscar();
  },
  methods: {
    ...mapActions('IntegracaoErp', ['listar', 'setFilters', 'clearFilters', 'excluir']),
    async buscar() {
      await this.setFilters({ ...this.filters, page: this.currentPage });
      await this.listar();
    },
    async limparFiltros() {
      this.filters = { q: '', status: null };
      this.currentPage = 1;
      await this.clearFilters();
      await this.listar();
    },
    onPageChange(page) {
      this.currentPage = page;
      this.buscar();
    },
    abrirNova() {
      this.$refs.modalForm.open(null);
    },
    editar(item) {
      this.$refs.modalForm.open(item);
    },
    async testar(item) {
      this.$refs.modalTeste.open(item);
    },
    async confirmarExcluir(item) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Excluir a integração "${item.nome}"? Esta ação não pode ser desfeita.`,
        {
          title: 'Confirmar exclusão',
          okTitle: 'Excluir',
          okVariant: 'danger',
          cancelTitle: 'Cancelar',
          centered: true,
        },
      );
      if (!ok) return;
      await this.excluir(item.integracaoid);
      this.$bvToast.toast('Integração excluída.', { title: 'OK', variant: 'success', solid: true });
    },
    onSaved() {
      this.buscar();
    },
    metodoVariant(m) {
      switch ((m || '').toUpperCase()) {
        case 'GET':    return 'light-info';
        case 'POST':   return 'light-success';
        case 'PUT':    return 'light-warning';
        case 'PATCH':  return 'light-warning';
        case 'DELETE': return 'light-danger';
        default:       return 'light-secondary';
      }
    },
    formatDate(v) {
      if (!v) return '';
      try {
        const d = new Date(v);
        return d.toLocaleString('pt-BR');
      } catch (e) { return v; }
    },
  },
};
</script>

<style scoped>
.page-breadcrumb { display: flex; align-items: center; font-size: 0.9rem; color: #6e6b7b; }
</style>
